/*
 * Freshly Baked — pickup date/time picker (global singleton).
 *
 * - Builds available pickup slots (Mon-Fri, business hours, fixed-size
 *   steps, minimum lead time, next N days) in the America/New_York
 *   timezone, using Intl so DST is handled automatically.
 * - Saves the chosen slot as three cart attributes via /cart/update.js:
 *     "Pickup Date"  -> human label, e.g. "Wed, Sep 23"
 *     "Pickup Time"  -> human label, e.g. "12:15 PM"
 *     "Pickup Slot"  -> ISO 8601 with explicit offset, e.g.
 *                       "2026-09-23T12:15:00-04:00" (source of truth)
 * - Gates checkout: intercepts clicks on the theme's checkout button(s)
 *   ([name="checkout"]) whenever the cart has a Freshly Baked item and
 *   re-validates the stored slot (existence + still has enough lead time)
 *   with a fresh /cart.js fetch before letting the click through.
 * - Also hides the dynamic/express checkout buttons (Shop Pay, PayPal,
 *   etc.) while a valid slot is missing, since those bypass the button
 *   above entirely. This is best-effort: Shopify does not let a classic
 *   (non Plus / non checkout-extensibility) theme block those buttons at
 *   the checkout step itself, only hide them in the cart.
 */
(function () {
  if (window.FbPickup) return;

  var ATTR_DATE = 'Pickup Date';
  var ATTR_TIME = 'Pickup Time';
  var ATTR_ISO = 'Pickup Slot';
  var ATTR_PHONE = 'Pickup Phone';
  var FB_MAX_PER_SLOT = 3;
  function fbIsFull(iso) { var el = document.querySelector('[data-fb-booked]'); if (!el || !iso) return false; var list = (el.getAttribute('data-fb-booked') || '').split(/[\s,|]+/); var n = 0; for (var i = 0; i < list.length; i++) { if (list[i] && list[i].slice(0, 16) === String(iso).slice(0, 16)) n++; } return n >= FB_MAX_PER_SLOT; }
  var ATTR_EMAIL = 'Pickup Email';
  function fbPhoneEl() { return document.querySelector('[data-fb-pickup-phone]'); }
  function fbEmailEl() { return document.querySelector('[data-fb-pickup-email]'); }
  function fbPhoneOk() { var el = fbPhoneEl(); if (!el) return true; return el.value.replace(/\D/g, '').length >= 10; }

  function toMinutes(hhmm) {
    var parts = (hhmm || '00:00').split(':');
    return Number(parts[0]) * 60 + Number(parts[1]);
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function readConfig() {
    var el = document.getElementById('fb-pickup-data');
    var data = (el && el.dataset) || {};
    return {
      leadMinutes: Number(data.leadMinutes) || 90,
      openMinutes: toMinutes(data.openHhMm || '11:30'),
      closeMinutes: toMinutes(data.closeHhMm || '15:30'),
      stepMinutes: Number(data.stepMinutes) || 15,
      daysAhead: Number(data.daysAhead) || 7,
      timeZone: data.timezone || 'America/New_York',
    };
  }

  var config = readConfig();

  /* ---------- timezone-aware date helpers ---------- */

  function nyPartsNow() {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: config.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });
    return { y: Number(out.year), m: Number(out.month), d: Number(out.day) };
  }

  // DST-safe UTC offset (in minutes) of the configured timezone on a given
  // calendar date, probed at noon UTC so we never land on a transition.
  function tzOffsetMinutes(y, m, d) {
    var probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: config.timeZone,
      timeZoneName: 'shortOffset',
    }).formatToParts(probe);
    var tzName = 'GMT-5';
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'timeZoneName') tzName = parts[i].value;
    }
    var match = tzName.match(/GMT([+-]\d+)(?::(\d+))?/);
    var hours = match ? Number(match[1]) : -5;
    var mins = match && match[2] ? Number(match[2]) : 0;
    return hours * 60 + (hours < 0 ? -mins : mins);
  }

  function slotAbsolute(y, m, d, minutesOfDay) {
    var offset = tzOffsetMinutes(y, m, d);
    var hh = Math.floor(minutesOfDay / 60);
    var mm = minutesOfDay % 60;
    var utcMillis = Date.UTC(y, m - 1, d, hh, mm, 0) - offset * 60000;
    return { millis: utcMillis, offset: offset, hh: hh, mm: mm };
  }

  function addDays(y, m, d, days) {
    var dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate(), dow: dt.getUTCDay() };
  }

  function isoWithOffset(y, m, d, hh, mm, offset) {
    var sign = offset <= 0 ? '-' : '+';
    var abs = Math.abs(offset);
    var oh = Math.floor(abs / 60);
    var om = abs % 60;
    return y + '-' + pad(m) + '-' + pad(d) + 'T' + pad(hh) + ':' + pad(mm) + ':00' + sign + pad(oh) + ':' + pad(om);
  }

  /* ---------- slot generation ---------- */

  function buildSchedule() {
    var now = nyPartsNow();
    var minMillis = Date.now() + config.leadMinutes * 60000;
    var lang = document.documentElement.lang || 'en';
    var dateFmt = new Intl.DateTimeFormat(lang, { timeZone: config.timeZone, weekday: 'short', month: 'short', day: 'numeric' });
    var timeFmt = new Intl.DateTimeFormat(lang, { timeZone: config.timeZone, hour: 'numeric', minute: '2-digit' });

    var days = [];
    for (var offset = 0; offset < config.daysAhead; offset++) {
      var cand = addDays(now.y, now.m, now.d, offset);
      if (cand.dow === 0 || cand.dow === 6) continue; // closed weekends

      var slots = [];
      for (var minutesOfDay = config.openMinutes; minutesOfDay <= config.closeMinutes; minutesOfDay += config.stepMinutes) {
        var abs = slotAbsolute(cand.y, cand.m, cand.d, minutesOfDay);
        if (abs.millis < minMillis) continue;
        var slotDate = new Date(abs.millis);
        slots.push({
          hh: abs.hh,
          mm: abs.mm,
          label: timeFmt.format(slotDate),
          iso: isoWithOffset(cand.y, cand.m, cand.d, abs.hh, abs.mm, abs.offset),
          millis: abs.millis,
        });
      }
      if (slots.length === 0) continue;

      days.push({
        y: cand.y,
        m: cand.m,
        d: cand.d,
        key: cand.y + '-' + pad(cand.m) + '-' + pad(cand.d),
        label: dateFmt.format(new Date(Date.UTC(cand.y, cand.m - 1, cand.d, 12))),
        slots: slots,
      });
    }
    return days;
  }

  /* ---------- cart helpers ---------- */

  function fetchCart() {
    return fetch(((window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||'/')+'cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      return res.json();
    });
  }

  function cartHasFreshlyBaked(cart) {
    return (cart.items || []).some(function (item) {
      return item.product_type === 'Freshly Baked';
    });
  }

  function currentSlotFromCart(cart) {
    var iso = cart.attributes && cart.attributes[ATTR_ISO];
    if (!iso) return null;
    var millis = Date.parse(iso);
    if (Number.isNaN(millis)) return null;
    return { iso: iso, millis: millis, date: cart.attributes[ATTR_DATE], time: cart.attributes[ATTR_TIME] };
  }

  function slotIsValid(slot) {
    if (!slot) return false;
    // small grace window so a slot doesn't flip invalid mid-render
    return slot.millis >= Date.now() + config.leadMinutes * 60000 - 60000;
  }

  function updateCartAttributes(day, slot) {
    var attributes = {};
    attributes[ATTR_DATE] = day.label;
    attributes[ATTR_TIME] = slot.label;
    attributes[ATTR_ISO] = slot.iso;
    if (fbPhoneEl()) attributes[ATTR_PHONE] = fbPhoneEl().value.trim();
    if (fbEmailEl()) attributes[ATTR_EMAIL] = fbEmailEl().value.trim();
    return fetch(((window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)||'/')+'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart update failed');
      return res.json();
    });
  }

  function refreshCartSections() {
    return fetch('?section_id=helper-cart')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newForm = doc.getElementById('AjaxCartForm');
        var newSubtotal = doc.getElementById('AjaxCartSubtotal');

        document.querySelectorAll('#AjaxCartForm').forEach(function (el) {
          if (!newForm) return;
          el.innerHTML = newForm.innerHTML;
          if (typeof el.ajaxifyCartItems === 'function') el.ajaxifyCartItems();
        });
        document.querySelectorAll('#AjaxCartSubtotal').forEach(function (el) {
          if (newSubtotal) el.innerHTML = newSubtotal.innerHTML;
        });
        document.querySelectorAll('[data-header-cart-count]').forEach(function (el) {
          var c = newForm && newForm.querySelector('[data-cart-count]');
          if (c) el.textContent = c.textContent;
        });
        document.querySelectorAll('[data-header-cart-total]').forEach(function (el) {
          var t = newForm && newForm.querySelector('[data-cart-total]');
          if (t) el.textContent = t.textContent;
        });
      })
      .catch(function (err) {
        console.error('[fb-pickup] could not refresh cart sections', err);
      });
  }

  function setDynamicCheckoutHidden(hidden) {
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
      el.classList.toggle('fb-pickup-hide', hidden);
    });
  }

  function refreshDynamicCheckoutVisibility() {
    fetchCart()
      .then(function (cart) {
        var needsSlot = cartHasFreshlyBaked(cart) && !slotIsValid(currentSlotFromCart(cart));
        setDynamicCheckoutHidden(needsSlot);
      })
      .catch(function () {});
  }

  /* ---------- picker UI ---------- */

  function Picker() {
    this.sidebar = document.getElementById('fb-pickup-sidebar');
    if (!this.sidebar) return;

    this.datesEl = this.sidebar.querySelector('[data-fb-pickup-dates]');
    this.timesEl = this.sidebar.querySelector('[data-fb-pickup-times]');
    this.noSlotsEl = this.sidebar.querySelector('[data-fb-pickup-no-slots]');
    this.errorEl = this.sidebar.querySelector('[data-fb-pickup-error]');
    this.changedEl = this.sidebar.querySelector('[data-fb-pickup-changed-notice]');
    this.confirmBtn = this.sidebar.querySelector('[data-fb-pickup-confirm]');

    this.schedule = [];
    this.selectedDay = null;
    this.selectedSlot = null;
    this.pendingCheckout = false;

    var self = this;

    this.confirmBtn.addEventListener('click', function () { self.confirm(); });
    if (fbPhoneEl()) fbPhoneEl().addEventListener('input', function () { self.confirmBtn.disabled = !(self.selectedDay && self.selectedSlot && fbPhoneOk()); });

    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-fb-pickup-open]');
      if (!opener) return;
      e.preventDefault();
      self.open({ pendingCheckout: false });
    });

    // Capture-phase so we run before the native form submission for the
    // "Checkout" button(s) — there can be more than one in the DOM at
    // once (cart drawer + cart page both render a copy of cart-subtotal).
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest('[name="checkout"]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        self.attemptCheckout();
      },
      true
    );
  }

  Picker.prototype.render = function () {
    this.errorEl.hidden = true;

    this.datesEl.innerHTML = '';
    var self = this;
    this.schedule.forEach(function (day) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fb-pickup__date-chip';
      btn.textContent = day.label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', String(self.selectedDay === day));
      if (self.selectedDay === day) btn.classList.add('is-selected');
      btn.addEventListener('click', function () {
        self.selectedDay = day;
        self.selectedSlot = null;
        self.render();
      });
      self.datesEl.appendChild(btn);
    });

    this.timesEl.innerHTML = '';
    var day = this.selectedDay;
    this.noSlotsEl.hidden = !day || day.slots.length > 0;
    if (day) {
      day.slots.forEach(function (slot) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fb-pickup__time-chip';
        btn.textContent = slot.label; if (fbIsFull(slot.iso)) { btn.disabled = true; btn.classList.add('is-full'); btn.setAttribute('aria-label', slot.label + ' (full)'); }
        btn.setAttribute('aria-pressed', String(self.selectedSlot === slot));
        if (self.selectedSlot === slot) btn.classList.add('is-selected');
        btn.addEventListener('click', function () {
          self.selectedSlot = slot;
          self.render();
        });
        self.timesEl.appendChild(btn);
      });
    }

    this.confirmBtn.disabled = !(this.selectedDay && this.selectedSlot && fbPhoneOk());
  };

  Picker.prototype.open = function (opts) {
    this.pendingCheckout = !!(opts && opts.pendingCheckout);
    this.schedule = buildSchedule();

    var self = this;
    fetchCart()
      .then(function (cart) { return currentSlotFromCart(cart); })
      .catch(function () { return null; })
      .then(function (current) {
        self.selectedDay = null;
        self.selectedSlot = null;

        if (current) {
          outer: for (var i = 0; i < self.schedule.length; i++) {
            var d = self.schedule[i];
            for (var j = 0; j < d.slots.length; j++) {
              if (d.slots[j].iso === current.iso) {
                self.selectedDay = d;
                self.selectedSlot = d.slots[j];
                break outer;
              }
            }
          }
        }

        if (!self.selectedDay && self.schedule.length > 0) {
          self.selectedDay = self.schedule[0];
        }

        self.changedEl.hidden = !(current && !self.selectedSlot);
        self.render();
        document.querySelectorAll('sidebar-drawer.sidebar--opened').forEach(function (el) {
          if (el !== self.sidebar && typeof el.hide === 'function') el.hide();
        });
        self.sidebar.show();
      });
  };

  Picker.prototype.confirm = function () {
    if (!this.selectedDay || !this.selectedSlot) return;
    var self = this;
    this.confirmBtn.disabled = true;
    this.confirmBtn.classList.add('is-loading');

    updateCartAttributes(this.selectedDay, this.selectedSlot)
      .then(function () { return refreshCartSections(); })
      .then(function () {
        refreshDynamicCheckoutVisibility();
        self.sidebar.hide();
        if (self.pendingCheckout) {
          window.location.href = '/checkout';
        }
      })
      .catch(function () {
        self.errorEl.textContent = self.errorEl.dataset.text || '';
        self.errorEl.hidden = false;
        self.confirmBtn.disabled = false;
      })
      .finally(function () {
        self.confirmBtn.classList.remove('is-loading');
      });
  };

  Picker.prototype.attemptCheckout = function () {
    var self = this;
    fetchCart()
      .then(function (cart) {
        if (!cartHasFreshlyBaked(cart)) {
          window.location.href = '/checkout';
          return;
        }
        var slot = currentSlotFromCart(cart);
        if (slotIsValid(slot)) {
          window.location.href = '/checkout';
          return;
        }
        self.open({ pendingCheckout: true });
      })
      .catch(function () {
        // If we can't reach the cart API, don't trap the shopper — let
        // Shopify's own checkout validation take over.
        window.location.href = '/checkout';
      });
  };

  function init() {
    window.FbPickup = new Picker();
    refreshDynamicCheckoutVisibility();
    setInterval(refreshDynamicCheckoutVisibility, 60000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) refreshDynamicCheckoutVisibility();
  });
})();